import { z } from "zod";
import Label from "../../../ui/Label/Label";
import ConfirmationModal from "../../../ui/Modal/ConfirmationModal";
import Modal from "../../../ui/Modal/Modal";
import TextInput from "../../../ui/TextInput/TextInput";
import styles from "./SetupShopifyModal.module.scss";
import { Controller, useForm } from "react-hook-form";
import { useToast } from "../../../ui/Toast/ToastContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { graphql } from "../../../__generated__/gql";
import { useMutation } from "urql";

const SetupShopifyConnection = graphql(/* GraphQL */ `
  mutation setupShopifyConnection($input: SetupShopifyConnectionInput!) {
    setupShopifyConnection(input: $input) {
      id
      shopDomain
    }
  }
`);

const schema = z.object({
  shopDomain: z.string(),
  accessToken: z.string(),
});

function SetupShopifyModal({
  isOpen,
  onClose,
  onSubmit: onSubmitProp,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });
  const { showToast } = useToast();

  const [, setupShopifyConnection] = useMutation(SetupShopifyConnection);

  const onSubmit = async ({
    shopDomain,
    accessToken,
  }: z.infer<typeof schema>) => {
    try {
      const urlObject = new URL(
        !shopDomain.startsWith("http://") && !shopDomain.startsWith("https://")
          ? `https://${shopDomain}`
          : shopDomain,
      );

      try {
        await fetch(urlObject.href, {
          method: "HEAD",
          mode: "no-cors",
        });
      } catch {
        showToast({
          title: "Shopify store can't be reached",
          description: "Please check the URL and try again",
          variant: "error",
        });
      }

      await setupShopifyConnection({
        input: {
          shopDomain: urlObject.hostname,
          accessToken,
        },
      });

      onSubmitProp();
    } catch {
      showToast({
        title: "Invalid store URL",
        description: "Please check the URL and try again",
        variant: "error",
      });

      return;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Setup Shopify connection"
      subtitle={
        <>
          For help configuring your Shopify connection, you can check out our{" "}
          <a
            href="https://docs.macawhq.com/en/articles/10975894-setting-up-shopify-connection"
            target="_blank"
            rel="noreferrer"
          >
            Shopify setup guide
          </a>
        </>
      }
    >
      <ConfirmationModal
        onSubmit={handleSubmit(onSubmit)}
        onClose={onClose}
        confirmDisabled={!isValid || isSubmitting}
        loading={isSubmitting}
      >
        <div className={styles.container}>
          <Controller
            control={control}
            name="shopDomain"
            render={({ field }) => (
              <Label label="Enter your Shopify store domain">
                <TextInput placeholder="shopname.myshopify.com" {...field} />
              </Label>
            )}
          />
          <Controller
            control={control}
            name="accessToken"
            render={({ field }) => (
              <Label label="Enter your Shopify access token">
                <TextInput
                  placeholder="shpat_00000000000000000000000000000000"
                  {...field}
                />
              </Label>
            )}
          />
        </div>
      </ConfirmationModal>
    </Modal>
  );
}

export default SetupShopifyModal;
